import { floor, isNil, round } from 'lodash';
import { Fund, RoleCode } from 'src/components/Exchange/types';
import { AGREEMENT_TYPES, UNIT } from 'src/settings/settings';
import { FundSummary, ItemSelection } from 'src/types';
import { currency } from '../utils';

export interface OptionItem extends ItemSelection {
  month: number;
  year: number;
}
/**
 * Check if a fund matches one of the IRA agreementTypes
 * @param {UserFund} UserFund
 * @returns boolean
 */
export const isIRA = ({ agreementType }: Fund) =>
  [
    AGREEMENT_TYPES.ROTH_ROLLOVER,
    AGREEMENT_TYPES.ROLLOVER_IRA,
    AGREEMENT_TYPES.ROTH_IRA,
    AGREEMENT_TYPES.TRADITIONAL_IRA,
    AGREEMENT_TYPES.ROTH_ROLLOVER_IRA,
  ].includes(agreementType);

/**
 * Check if a fund matches a the IRA expectations
 * @param {UserFund} UserFund
 * @returns boolean
 */
export const isReccuringExchangeIRAMatching = ({ agreementType }: Fund) =>
  [
    AGREEMENT_TYPES.INDIVIDUAL,
    AGREEMENT_TYPES.TRANSFER_ON_DEATH,
    AGREEMENT_TYPES.JOINT_TENANT,
  ].includes(agreementType);

/**
 * Check if a fund is a tax responsible party and that there is an owner
 * @param {UserFund} UserFund
 * @returns boolean
 */
export const isPrimaryParty = ({ agreementParties = [] }: Fund) =>
  agreementParties.some(
    ({ roleCode, primaryParty }) =>
      roleCode === RoleCode.OWNER && primaryParty === 'Y'
  );

export function calculateGrossDollarsShares(
  grossAmount: number,
  grossUnit: UNIT,
  sharePrice: number
) {
  if (grossUnit === UNIT.DOLLARS && !isNil(grossAmount) && sharePrice) {
    return floor(grossAmount / sharePrice, 3);
  }
  return 0;
}

export function calculateGrossPercentShares(
  availableShares: number,
  grossAmount: number,
  grossUnit: UNIT
) {
  if (grossUnit === UNIT.PERCENT && !isNil(grossAmount) && availableShares) {
    return floor((availableShares * grossAmount) / 100, 3);
  }
  return 0;
}

export const calculateGrossAmountDollars = (
  equityAsOf: number,
  grossAmount: number,
  grossUnit: UNIT,
  sharePrice: number
) => {
  switch (grossUnit) {
    case UNIT.DOLLARS:
      return grossAmount;
    case UNIT.PERCENT:
      return round(equityAsOf * grossAmount) / 100;
    case UNIT.SHARES:
      return round(sharePrice * grossAmount, 2);
    case UNIT.ALL_SHARES:
      return equityAsOf;
    default:
      return;
  }
};

export const getExchangeTotals = (exchangeFromSummaryFundList: FundSummary[]) =>
  currency(
    exchangeFromSummaryFundList.reduce(
      (sum, n) => sum + n.grossAmountDollars,
      0
    )
  );

export const optionSorter = (x: OptionItem, y: OptionItem) =>
  x.year === y.year ? x.month - y.month : x.year - y.year;
